import { lazy } from 'react';
import { Route, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { ProtectedRoute, PublicOnlyRoute, RoleRoute } from './guards';

/**
 * Route map (Task 04).
 *
 * Built with `createBrowserRouter` — a **data router** — rather than `<BrowserRouter>`.
 * That is not a style preference: `useBlocker`, which the unsaved-changes guard depends on,
 * only exists under a data router. With the older router it throws on render and takes the
 * whole page down with it, which is exactly what the invoice editor hit in Task 12.
 *
 * Every page is a lazy chunk so the initial download stays small — staff frequently work on
 * mobile connections on location (BRD C-03).
 */

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const ChangePasswordPage = lazy(() => import('@/features/auth/ChangePasswordPage'));
const ActivateAccountPage = lazy(() => import('@/features/auth/ActivateAccountPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const QuickBookingPage = lazy(() => import('@/features/bookings/QuickBookingPage'));
const CustomersPage = lazy(() => import('@/features/customers/CustomersPage'));
const CustomerDetailPage = lazy(() => import('@/features/customers/CustomerDetailPage'));
const CustomerFormPage = lazy(() => import('@/features/customers/CustomerFormPage'));
const EventsPage = lazy(() => import('@/features/events/EventsPage'));
const EventCalendarPage = lazy(() => import('@/features/events/EventCalendarPage'));
const EventDetailPage = lazy(() => import('@/features/events/EventDetailPage'));
const EventFormPage = lazy(() => import('@/features/events/EventFormPage'));
const InvoicesPage = lazy(() => import('@/features/invoices/InvoicesPage'));
const InvoiceEditorPage = lazy(() => import('@/features/invoices/InvoiceEditorPage'));
const BillsPage = lazy(() => import('@/features/bills/BillsPage'));
const BillEditorPage = lazy(() => import('@/features/bills/BillEditorPage'));
const DirectBillPage = lazy(() => import('@/features/bills/DirectBillPage'));
const PaymentsPage = lazy(() => import('@/features/payments/PaymentsPage'));
const ServicesPage = lazy(() => import('@/features/services/ServicesPage'));
const OutstandingReportPage = lazy(() => import('@/features/reports/OutstandingReportPage'));
const RetentionReportPage = lazy(() => import('@/features/reports/RetentionReportPage'));
const LedgerPage = lazy(() => import('@/features/ledger/LedgerPage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const UsersPage = lazy(() => import('@/features/users/UsersPage'));
const AuditLogPage = lazy(() => import('@/features/audit/AuditLogPage'));
const ForbiddenPage = lazy(() => import('@/features/errors/ForbiddenPage'));
const NotFoundPage = lazy(() => import('@/features/errors/NotFoundPage'));

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route element={<PublicOnlyRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/*
       * Reached from an emailed link, so outside BOTH guards.
       *
       * Not under `PublicOnlyRoute` either: that redirects an authenticated visitor to the
       * dashboard, which would silently swallow the link for anyone still signed in on the
       * device — exactly the person resetting a password they think is compromised.
       *
       * These paths must match what the server's mailer builds. See `linkFor`.
       */}
      <Route path="/activate" element={<ActivateAccountPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route element={<ProtectedRoute />}>
        <Route path="/change-password" element={<ChangePasswordPage />} />

        <Route element={<AppLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="quick-booking" element={<QuickBookingPage />} />

          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/new" element={<CustomerFormPage />} />
          <Route path="customers/:id" element={<CustomerDetailPage />} />
          <Route path="customers/:id/edit" element={<CustomerFormPage />} />

          <Route path="events" element={<EventsPage />} />
          <Route path="events/new" element={<EventFormPage />} />
          <Route path="events/calendar" element={<EventCalendarPage />} />
          <Route path="events/:id" element={<EventDetailPage />} />
          <Route path="events/:id/edit" element={<EventFormPage />} />

          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="invoices/new" element={<InvoiceEditorPage />} />
          <Route path="invoices/:id" element={<InvoiceEditorPage />} />

          <Route path="bills" element={<BillsPage />} />
          <Route path="bills/new" element={<BillEditorPage />} />
          <Route path="bills/direct" element={<DirectBillPage />} />
          <Route path="bills/:id" element={<BillEditorPage />} />

          <Route path="payments" element={<PaymentsPage />} />
          <Route path="services" element={<ServicesPage />} />

          <Route path="reports/outstanding" element={<OutstandingReportPage />} />
          <Route path="reports/retention" element={<RetentionReportPage />} />

          <Route path="ledger/:customerId" element={<LedgerPage />} />

          {/* Admin-only branch — FRD §3.2 */}
          <Route element={<RoleRoute roles={['ADMIN']} />}>
            <Route path="settings" element={<SettingsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="audit-logs" element={<AuditLogPage />} />
          </Route>

          <Route path="forbidden" element={<ForbiddenPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Route>,
  ),
);
